import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useStore } from "./StoreContext";
import { ShoppingBag, User, MapPin, Search, Home as HomeIcon, Menu, X } from "lucide-react";

export function StorefrontLayout({ children }) {
  const { itemCount, customer } = useStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { setMenuOpen(false); }, [loc.pathname]);

  return (
    <div className="min-h-screen bg-amber-50/30 text-stone-900" data-testid="storefront-layout">
      {/* SEO defaults: page-specific titles set via useDocumentTitle in pages */}
      <Header itemCount={itemCount} customer={customer} onMenu={() => setMenuOpen(v => !v)} menuOpen={menuOpen} />
      <main className="pb-24 sm:pb-10">{children}</main>
      <MobileTabBar itemCount={itemCount} />
      <Footer />
    </div>
  );
}

function Header({ itemCount, customer, onMenu, menuOpen }) {
  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
        <Link to="/" className="flex items-center gap-2 font-heading font-black text-2xl tracking-tight" data-testid="store-logo">
          <span className="inline-block w-8 h-8 rounded-full bg-amber-600 text-white grid place-items-center text-base">F</span>
          <span>Furmigo</span>
        </Link>
        <nav className="hidden sm:flex items-center gap-1 ml-6 flex-1">
          <TopLink to="/products">Shop</TopLink>
          <TopLink to="/stores">Find a store</TopLink>
          <TopLink to="/book">Book grooming</TopLink>
        </nav>
        <div className="ml-auto flex items-center gap-1">
          <Link to="/products" className="p-2 rounded-md hover:bg-stone-100 sm:hidden" data-testid="store-search-icon"><Search className="w-5 h-5" /></Link>
          <Link to="/account" className="p-2 rounded-md hover:bg-stone-100" data-testid="store-account-icon">
            <User className="w-5 h-5" />
          </Link>
          <Link to="/cart" className="p-2 rounded-md hover:bg-stone-100 relative" data-testid="store-cart-icon">
            <ShoppingBag className="w-5 h-5" />
            {itemCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-amber-600 text-white text-[10px] font-bold w-5 h-5 grid place-items-center rounded-full" data-testid="cart-count">{itemCount}</span>
            )}
          </Link>
          <button onClick={onMenu} className="p-2 rounded-md hover:bg-stone-100 sm:hidden" data-testid="store-menu-toggle">
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {menuOpen && (
        <div className="sm:hidden border-t border-stone-200 px-4 py-2 space-y-1 bg-white">
          <MobileLink to="/products">Shop</MobileLink>
          <MobileLink to="/stores">Find a store</MobileLink>
          <MobileLink to="/book">Book grooming</MobileLink>
          <MobileLink to="/account">{customer ? "My account" : "Login"}</MobileLink>
        </div>
      )}
    </header>
  );
}

const TopLink = ({ to, children }) => (
  <NavLink to={to} className={({ isActive }) => `px-3 py-2 text-sm font-semibold rounded-md ${isActive ? "text-amber-700" : "text-stone-700 hover:bg-stone-100"}`} data-testid={`top-link-${to.slice(1)}`}>
    {children}
  </NavLink>
);
const MobileLink = ({ to, children }) => (
  <Link to={to} className="block px-3 py-2 text-base font-semibold rounded-md hover:bg-stone-100" data-testid={`mobile-link-${to.slice(1) || "home"}`}>
    {children}
  </Link>
);

function MobileTabBar({ itemCount }) {
  return (
    <nav className="sm:hidden fixed bottom-0 inset-x-0 z-40 border-t border-stone-200 bg-white/95 backdrop-blur-md">
      <div className="grid grid-cols-4 text-xs">
        <TabItem to="/" icon={<HomeIcon className="w-5 h-5" />} label="Home" testid="tab-home" />
        <TabItem to="/products" icon={<Search className="w-5 h-5" />} label="Shop" testid="tab-shop" />
        <TabItem to="/cart" icon={<ShoppingBag className="w-5 h-5" />} label={`Cart${itemCount > 0 ? ` (${itemCount})` : ""}`} testid="tab-cart" />
        <TabItem to="/account" icon={<User className="w-5 h-5" />} label="Me" testid="tab-account" />
      </div>
    </nav>
  );
}
const TabItem = ({ to, icon, label, testid }) => (
  <NavLink to={to} end={to === "/"} className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2.5 ${isActive ? "text-amber-700" : "text-stone-600"}`} data-testid={testid}>
    {icon}<span className="font-medium">{label}</span>
  </NavLink>
);

function Footer() {
  return (
    <footer className="border-t border-stone-200 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid sm:grid-cols-3 gap-6 text-sm">
        <div>
          <div className="font-heading text-xl font-black mb-2">Furmigo</div>
          <div className="text-stone-500">Pet grooming and essentials in one roof.</div>
        </div>
        <div>
          <div className="font-semibold mb-1">Shop</div>
          <Link to="/products" className="block text-stone-500 hover:text-stone-900">All products</Link>
          <Link to="/stores" className="block text-stone-500 hover:text-stone-900">Find a store</Link>
          <Link to="/book" className="block text-stone-500 hover:text-stone-900">Book grooming</Link>
        </div>
        <div>
          <div className="font-semibold mb-1 flex items-center gap-1.5"><MapPin className="w-4 h-4" />Find us</div>
          <div className="text-stone-500">India • furmigo.shop</div>
        </div>
      </div>
      <div className="text-center text-xs text-stone-400 py-4 border-t border-stone-100">© {new Date().getFullYear()} Furmigo. All rights reserved.</div>
    </footer>
  );
}

// SEO helper
export function useDocumentTitle(title, description) {
  useEffect(() => {
    if (title) document.title = `${title} · Furmigo — Pet grooming and essentials in one roof`;
    if (description) {
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "description";
        document.head.appendChild(meta);
      }
      meta.content = description;
    }
  }, [title, description]);
}
